import React, { useState, useEffect, useContext } from 'react';
import './RatingScaleOptions.scss';
import { FormInput } from '../../Form/Input/FormInput';
import { SelectBox } from '../../Pickers/SelectBox/SelectBox';
import { RatingScaleDisplay } from '../RatingScaleDisplay/RatingScaleDisplay';
import { I18nReactContext } from '../../../contexts/I18nContext';
import { QuestionnaireReactContext } from '../../../contexts/QuestionnaireContext/QuestionnaireContext';
import { useContextSelector } from 'use-context-selector';
import { get } from 'lodash';
import useDebounce from '../../../Hooks/useDebounce';

/**
 * RatingScaleQuestion Component
 * Handles the configuration of rating scale questions for surveys
 */
export const RatingScaleOptions = ({ questionId }) => {
    const { i18n } = useContext(I18nReactContext);
    const updateQuestion = useContextSelector(QuestionnaireReactContext, context => context.updateQuestion);
    const question = useContextSelector(QuestionnaireReactContext, context => context.getQuestionById({ questionId }));
    const activeQuestionId = useContextSelector(QuestionnaireReactContext, context => context.activeQuestionId);
    
    // Get current rating scale config or set defaults
    const currentConfig = get(question, 'ratingScaleConfig', {});
    
    const [minValue, setMinValue] = useState(get(currentConfig, 'minValue', 1));
    const [maxValue, setMaxValue] = useState(get(currentConfig, 'maxValue', 5));
    const [minLabel, setMinLabel] = useState(get(currentConfig, 'minLabel', ''));
    const [maxLabel, setMaxLabel] = useState(get(currentConfig, 'maxLabel', ''));
    
    // Debounce the values to avoid excessive updates
    const debouncedMinValue = useDebounce({value: minValue, delay: 500});
    const debouncedMaxValue = useDebounce({value: maxValue, delay: 500});
    const debouncedMinLabel = useDebounce({value: minLabel, delay: 500});
    const debouncedMaxLabel = useDebounce({value: maxLabel, delay: 500});

    // Create options for the dropdowns
    const minValueOptions = [
        { value: 0, text: '0' },
        { value: 1, text: '1' }
    ];

    const maxValueOptions = [
        { value: 2, text: '2' },
        { value: 3, text: '3' },
        { value: 4, text: '4' },
        { value: 5, text: '5' },
        { value: 6, text: '6' },
        { value: 7, text: '7' },
        { value: 8, text: '8' },
        { value: 9, text: '9' },
        { value: 10, text: '10' }
    ];

    // Update question when debounced values change
    useEffect(() => {
        if (activeQuestionId === questionId) {
            const ratingScaleConfig = {
                minValue: debouncedMinValue,
                maxValue: debouncedMaxValue,
                minLabel: debouncedMinLabel || i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMinLabel'),
                maxLabel: debouncedMaxLabel || i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMaxLabel'),
            };
            
            updateQuestion({ 
                text: get(question, 'text', ''), 
                type: get(question, 'type', ''), 
                ratingScaleConfig 
            });
        }
    }, [debouncedMinValue, debouncedMaxValue, debouncedMinLabel, debouncedMaxLabel, activeQuestionId, questionId, i18n, updateQuestion, question]);

    const handleMinValueChange = (e) => {
        const value = parseInt(e.target.value);
        if (value >= 0 && value <= 1 && value < maxValue) {
            setMinValue(value);
        }
    };

    const handleMaxValueChange = (e) => {
        const value = parseInt(e.target.value);
        if (value >= 2 && value <= 10 && value > minValue) {
            setMaxValue(value);
        }
    };



    const isActive = activeQuestionId === questionId;

    return (
        <div className={`rating-scale-options ${isActive ? 'focused' : ''}`}>
            <div className="rating-scale-config">
                <div className="config-row values-row">
                    <div className="config-field">
                        <SelectBox
                            label={String(i18n.translate('questionnaireQuestions.survey.ratingScale.minValue'))}
                            value={minValue}
                            onChange={handleMinValueChange}
                            options={minValueOptions}
                            dataTestId="rating-scale-min-value-select"
                        />
                    </div>
                    <div className="config-field">
                        <SelectBox
                            label={String(i18n.translate('questionnaireQuestions.survey.ratingScale.maxValue'))}
                            value={maxValue}
                            onChange={handleMaxValueChange}
                            options={maxValueOptions}
                            dataTestId="rating-scale-max-value-select"
                        />
                    </div>
                </div>

                <div className="config-row labels-row">
                    <div className="config-field">
                        <FormInput
                            label={i18n.translate('questionnaireQuestions.survey.ratingScale.minLabel')}
                            value={minLabel}
                            onChange={(e) => setMinLabel(e.target.value)}
                            placeholder={i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMinLabel')}
                            inputProps={{ maxLength: 10 }}
                            dataTestId="rating-scale-min-label-input"
                        />
                    </div>
                    <div className="config-field">
                        <FormInput
                            label={i18n.translate('questionnaireQuestions.survey.ratingScale.maxLabel')}
                            value={maxLabel}
                            onChange={(e) => setMaxLabel(e.target.value)}
                            placeholder={i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMaxLabel')}
                            inputProps={{ maxLength: 10 }}
                            dataTestId="rating-scale-max-label-input"
                        />
                    </div>
                </div>

                <div className="scale-preview" data-testid="rating-scale-preview">
                    <div className="preview-header">
                        <span className="preview-title">{i18n.translate('questionnaireQuestions.survey.ratingScale.previewTitle')}</span>
                    </div>
                    <RatingScaleDisplay
                        minValue={minValue}
                        maxValue={maxValue}
                        minLabel={String(minLabel || i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMinLabel'))}
                        maxLabel={String(maxLabel || i18n.translate('questionnaireQuestions.survey.ratingScale.defaultMaxLabel'))}
                        isPreview={true}
                        selectedValue={null}
                        onValueSelect={null}
                    />
                </div>
            </div>
        </div>
    );
}; 